import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Card,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  FileExcelOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  CheckOutlined,
  ReloadOutlined,
  CloseOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;


const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return '';

  const [startHour, startMinute] = startTime.split(':').map(Number);

  // Support formats like "2h 30m" or "1.5"
  const match = duration.match(/(\d+)h\s*(\d+)?m?/);
  let hours = 0, minutes = 0;

  if (match) {
    hours = parseInt(match[1], 10);
    minutes = parseInt(match[2] || '0', 10);
  }

  let endHour = startHour + hours;
  let endMinute = startMinute + minutes;

  if (endMinute >= 60) {
    endHour += Math.floor(endMinute / 60);
    endMinute %= 60;
  }

  return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
};


const TutorClassLog = () => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['50', '100', '500'],
    showTotal: (total) => `${t('Total')}: ${total} ${t('classes')}`,
  });
const today = moment().format('YYYY-MM-DD');
const [startDate, setStartDate] = useState(today);
const [endDate, setEndDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState(null);
  const [adminapproveFilter, setAdminapproveFilter] = useState(null);
  const [tutorFilter, setTutorFilter] = useState(null);
  const [summaryStats, setSummaryStats] = useState({
    totalClasses: 0,
    confirmedClasses: 0,
    pendingClasses: 0,
    adminConfirmed: 0
  });

const fetchClasses = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/classes/class-by-day`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate, endDate }   
      }
    );
    setClasses(response.data);
    setFilteredClasses(response.data);
    setLoading(false);
    calculateSummaryStats(response.data);
  } catch (error) {
    console.error('Error fetching classes:', error);
    message.error(t('Failed to fetch classes'));
    setLoading(false);
  }
}, [t, startDate, endDate]);

useEffect(() => {
  fetchClasses();
}, [fetchClasses]);

  

  const calculateSummaryStats = (data) => {
    const stats = {
      totalClasses: data.length,
      confirmedClasses: data.filter(cls => cls.status === 'approved').length,
      pendingClasses: data.filter(cls => cls.status !== 'approved').length,
      adminConfirmed: data.filter(cls => cls.adminapprove === 'adminconfirm').length
    };
    setSummaryStats(stats);
  };

useEffect(() => {
  const applyFilters = () => {
    const filtered = classes.filter((cls) => {
      const classDateStr = moment(cls.date).format('YYYY-MM-DD');
      const classDate = moment(classDateStr, 'YYYY-MM-DD');

      const startStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
      const endStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

      const isInRange =
        (!startStr || classDate.isSameOrAfter(startStr, 'day')) &&
        (!endStr || classDate.isSameOrBefore(endStr, 'day'));

      const isStatusMatch = statusFilter ? cls.status === statusFilter : true;
      const isAdminapproveMatch = adminapproveFilter ? cls.adminapprove === adminapproveFilter : true;
      const isTutorMatch = tutorFilter ? cls.nickname === tutorFilter : true;

      return isInRange && isStatusMatch && isAdminapproveMatch && isTutorMatch;
    }).sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      if (dateA !== dateB) return dateA - dateB;

      const timeA = new Date(`1970-01-01T${a.schedule_time}`).getTime();
      const timeB = new Date(`1970-01-01T${b.schedule_time}`).getTime();
      return timeA - timeB;
    });

    // 🟢 Keep current page, only update total count
    setFilteredClasses(filtered);
    setPagination(prev => ({
      ...prev,
      total: filtered.length
    }));

    calculateSummaryStats(filtered);
  };

  applyFilters();
}, [startDate, endDate, statusFilter, adminapproveFilter, tutorFilter, classes]);


const handleAdminUnapprove = async (classId) => {
  try {
    const token = localStorage.getItem('token');
    await axios.put(`${process.env.REACT_APP_API_URL}/teacher-hours/${classId}/admin-unapprove`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    // Update the class state with the new approval status
    setClasses(prevClasses => prevClasses.map(cls => 
      cls.classid === classId ? { ...cls, adminapprove: 'pending' } : cls
    ));
    
    message.success(t('Admin approval revoked successfully'));
  } catch (error) {
    console.error('Error revoking admin approval:', error);
    message.error(t('Failed to revoke admin approval'));
  }
};


  const getUniqueTutors = () => {
    const tutors = classes.map(cls => cls.nickname);
    return [...new Set(tutors)].sort();
  };

  const handleAdminApprove = async (classId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL}/classes/${classId}/adminapprove`, {
        adminapprove: status
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Update the class state with the new approval status
      setClasses(prevClasses => prevClasses.map(cls => 
        cls.classid === classId ? { ...cls, adminapprove: status } : cls
      ));
      
      message.success(t('Class approved successfully'));
    } catch (error) {
      console.error('Error confirming hours:', error);
      message.error(t('Failed to approve class'));
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const formatTime = (timeString) => {
    return moment(`1970-01-01T${timeString}`).format('HH:mm');
  };

const csvData = classes.map(cls => ({
  No: cls.classid,
  Date: new Date(cls.date).toLocaleDateString(),
  ClassCode: cls.class_code,
  ClassID: cls.classid,
  Subject: cls.subject_name,
  Teacher: `${cls.nickname} (${cls.teacher_name})`,
  Time: formatTime(cls.schedule_time),
  ScheduleHour: cls.schedule_hour,
  Status: cls.status === 'approved' ? t('confirmed') : t('confirmpending'),
  AdminApprove: cls.adminapprove === 'adminconfirm' ? t('Admin Confirmed') : t('Pending'),
  ApprovedBy: cls.admin_nickname || '-'   
}));


  const formatDate = (dateString) => {
    return moment(dateString).format('DD MMM YYYY');
  };


  const columns = [
    {
      title: t('No'),
      dataIndex: 'classid',
      key: 'no',
      width: 70,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => (
        <Space>
          <CalendarOutlined />
          <Text>{formatDate(date)}</Text>
        </Space>
      ),
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    },
    {
        title: t('time'),
        dataIndex: 'schedule_time',
        key: 'time',
        width: 120,
        render: (time, record) => (
          <Space direction="vertical" size={0}>
            <Text>
              <ClockCircleOutlined /> {formatTime(time)} - {calculateEndTime(time, record.schedule_hour)}
            </Text>

          </Space>
        ),
      },

    {
      title: t('Hour'),
      dataIndex: 'schedule_hour',
      key: 'hour',
      width: 80,
      render: (hours) => <Tag color="purple">{hours}</Tag>,
    },
        {
      title: t('classid'),
      dataIndex: 'classid',
      key: 'classid',
      width: 80,
    },
    {
      title: t('classcode'),
      dataIndex: 'class_code',
      key: 'classcode',
      width: 120,
      render: (code) => <Tag color="blue">{code}</Tag>,
    },

    {
      title: t('subject'),
      dataIndex: 'subject_name',
      key: 'subject',
      width: 150,
      render: (subject) => (
        <Tooltip title={subject}>
          <Space>
            <BookOutlined />
            <Text ellipsis style={{ maxWidth: 120 }}>{subject}</Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: t('teacher'),
      dataIndex: 'nickname',
      key: 'teacher',
      width: 120,
      render: (nickname) => (
        <Space>
          <UserOutlined />
          <Text>{nickname}</Text>
        </Space>
      ),
      filters: getUniqueTutors().map(tutor => ({ text: tutor, value: tutor })),
      onFilter: (value, record) => record.nickname === value,
    },
   
    {
      title: t('confirmteach'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        return status === 'approved' ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('HOURSAPPROVED')}
          </Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            {t('confirmpending')}
          </Tag>
        );
      },
      filters: [
        { text: t('confirmed'), value: 'approved' },
        { text: t('confirmpending'), value: 'pending' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
  title: t('admin confirm'),
  dataIndex: 'adminapprove',
  key: 'adminapprove',
  width: 200,
  render: (status, record) => {
    if (status === 'adminconfirm') {
      return (
        <Space>
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('Admin Confirmed')}
          </Tag>
          <Popconfirm
            title={t('Revoke Admin Approval')}
            description={t('Are you sure you want to revoke admin approval for this class?')}
            okText={t('Yes')}
            cancelText={t('No')}
            okType="danger"
            onConfirm={() => handleAdminUnapprove(record.classid)}
          >
            <Button
              danger
              size="small"
              shape="circle"
              icon={<CloseOutlined />}
              title={t('Unconfirm')}
            />
          </Popconfirm>
        </Space>
      );
    }

    // Default — Pending (not confirmed yet)
    return (
      <Popconfirm
        title={t('Confirm Admin Approval')}
        description={t('Are you sure you want to confirm this class?')}
        onConfirm={() => handleAdminApprove(record.classid, 'adminconfirm')}
        okText={t('Yes')}
        cancelText={t('No')}
      >
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
        >
          {t('Admin Confirm')}
        </Button>
      </Popconfirm>
    );
  },
  filters: [
    { text: t('Admin Confirmed'), value: 'adminconfirm' },
    { text: t('Pending'), value: 'pending' },
  ],
  onFilter: (value, record) => record.adminapprove === value,
},
          {
          title: t('approved by'),
          dataIndex: 'admin_nickname',
          key: 'admin_nickname',
          width: 140,
          render: (nickname) => nickname || '-',
        },

  ];

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Total Classes')}
              value={summaryStats.totalClasses}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Confirmed Classes')}
              value={summaryStats.confirmedClasses}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Pending Classes')}
              value={summaryStats.pendingClasses}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Admin Confirmed')}
              value={summaryStats.adminConfirmed}
              valueStyle={{ color: '#722ed1' }}
              prefix={<CheckOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Export */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={18}>
              <Space wrap size="middle">
              <Space>
                  <Text strong><CalendarOutlined /> {t('Date Range')}:</Text>
                  
                  {/* Start Date */}
                  <div>
                    <input
                      type="date"
                      id="hidden-start-date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ display: 'none' }}
                    />
                    <input
                      type="text"
                      readOnly
                      value={formatDate(startDate)}
                      placeholder="27-Jul-25"
                      onClick={() => (document.getElementById('hidden-start-date') as any)?.showPicker()}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9', cursor: 'pointer', width: '120px', textAlign: 'center' }}
                    />
                  </div>

                  <span>-</span>

                  {/* End Date */}
                  <div>
                    <input
                      type="date"
                      id="hidden-end-date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      style={{ display: 'none' }}
                    />
                    <input
                      type="text"
                      readOnly
                      value={formatDate(endDate)}
                      placeholder="27-Jul-25"
                      onClick={() => (document.getElementById('hidden-end-date') as any)?.showPicker()}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9', cursor: 'pointer', width: '120px', textAlign: 'center' }}
                    />
                  </div>
                </Space>
                <Space>
                  <Text strong><UserOutlined /> {t('Tutor')}:</Text>
                  <Select
                    style={{ width: 150 }}
                    value={tutorFilter}
                    onChange={setTutorFilter}
                    placeholder={t('Select Tutor')}
                    allowClear
                    showSearch
                  >
                    <Option value="">{t('All Tutors')}</Option>
                    {getUniqueTutors().map(tutor => (
                      <Option key={tutor} value={tutor}>
                        {tutor}
                      </Option>
                    ))}
                  </Select>
                </Space>

                <Space>
                  <Text strong><CheckCircleOutlined /> {t('Status')}:</Text>
                  <Select
                    style={{ width: 150 }}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder={t('Select Status')}
                    allowClear
                  >
                    <Option value="">{t('All Statuses')}</Option>
                    <Option value="approved">{t('confirmed')}</Option>
                    <Option value="pending">{t('confirmpending')}</Option>
                  </Select>
                </Space>

                <Space>
                  <Text strong><CheckOutlined /> {t('Admin Status')}:</Text>
                  <Select
                    style={{ width: 180 }}
                    value={adminapproveFilter}
                    onChange={setAdminapproveFilter}
                    placeholder={t('Select Admin Status')}
                    allowClear
                  >
                    <Option value="">{t('All Statuses')}</Option>
                    <Option value="adminconfirm">{t('Admin Confirmed')}</Option>
                    <Option value="pending">{t('Pending')}</Option>
                  </Select>
                </Space>
                <Space>
                   <Button 
                    icon={<ReloadOutlined />} 
                    onClick={fetchClasses} 
                    loading={loading}
                  >
                    {t('Refresh')}
                  </Button>
                </Space>
              </Space>
            </Col>
            
            <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }}>
              <CSVLink 
  data={csvData}
  filename={`tutor_class_log_${startDate}_to_${endDate}.csv`}
  style={{ textDecoration: 'none' }}
>
  <Button 
    type="primary" 
    icon={<FileExcelOutlined />}
  >
    {t('exportToCSV')}
  </Button>
</CSVLink>

            </Col>
          </Row>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredClasses}
          rowKey="classid"
          pagination={pagination}
          onChange={handleTableChange}
          loading={loading}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered
        />
      </Card>
    </div>
  );
};

export default withRoleAccess(TutorClassLog, ['superadmin', 'admin']);